import { Link } from "react-router-dom";
import { Seo } from "@/components/Seo/Seo";
import { Reveal } from "@/components/Reveal/Reveal";
import { Parallax } from "@/components/Motion/Parallax";
import { Marquee } from "@/components/Marquee/Marquee";
import { InquiryForm } from "@/components/Forms/InquiryForm";
import { FEATURED } from "@/data/featured";
import { EVENT_TYPES } from "@/data/content";
import { getMedia, mediaUrl } from "@/lib/media";
import { breadcrumbJsonLd } from "@/lib/structuredData";
import styles from "./BookEvent.module.css";

const TRUST = [
  "Full planning & coordination",
  "30 private acres",
  "Up to 16 overnight guests",
  "Reply within 24 hours",
];

export function BookEventPage() {
  const eventPhotos = getMedia("wedding").map((p) => ({
    src: mediaUrl(p.path),
    alt: p.name,
  }));
  const mid = Math.ceil(eventPhotos.length / 2);
  const rowOne = eventPhotos.slice(0, mid);
  const rowTwo = eventPhotos.slice(mid);

  return (
    <>
      <Seo
        title="Book an Event"
        description="Host your wedding, retreat, or celebration at Villa Rosa — a 30-acre Fallbrook estate with a panoramic ceremony space and full event planning and coordination services."
        path="/book-an-event"
        image={mediaUrl(FEATURED.ceremony)}
        jsonLd={breadcrumbJsonLd([
          { name: "Home", path: "/" },
          { name: "Book an Event", path: "/book-an-event" },
        ])}
      />

      {/* ---- Hero ---- */}
      <header className={styles.hero}>
        <Parallax
          speed={0.12}
          className={styles.heroMedia}
          style={{ backgroundImage: `url(${mediaUrl(FEATURED.ceremony)})` }}
        >
          <span />
        </Parallax>
        <div className={styles.heroScrim} />
        <div className={`container ${styles.heroInner}`}>
          <p className={`eyebrow eyebrow--rule ${styles.heroEyebrow}`}>
            Weddings &amp; Events
          </p>
          <h1 className={styles.heroTitle}>A backdrop worth the journey.</h1>
          <p className={styles.heroLede}>
            A breathtaking 30-acre setting for weddings, corporate retreats,
            reunions, and celebrations — with planning and coordination services
            to make it seamless.
          </p>
          <div className={styles.heroCtas}>
            <a href="#inquire" className="btn btn--primary">
              Request your date
            </a>
            <Link to="/gallery" className="btn btn--light">
              View the gallery
            </Link>
          </div>
        </div>
      </header>

      {/* ---- Trust strip ---- */}
      <div className={styles.trustBar}>
        {TRUST.map((item) => (
          <span key={item} className={styles.trustItem}>
            {item}
          </span>
        ))}
      </div>

      {/* ---- Editorial intro (ties the page to the rest of the site) ---- */}
      <section className={styles.chapter}>
        <div className={`container ${styles.chapterGrid}`}>
          <Reveal as="div" variant="up" className={styles.chapterText}>
            <p className="eyebrow eyebrow--rule">The Setting</p>
            <h2>The entire estate is yours.</h2>
            <p>
              Set on 30 private acres in the Fallbrook hills, Villa Rosa hands
              you the entire property: a panoramic ceremony space, lawns and
              decks for the celebration, a resort-style pool, and a four-bedroom
              villa to keep everyone close.
            </p>
            <p>
              No shared grounds and no rigid packages — just room to shape the
              day entirely around you, with a team to handle design,
              coordination, and catering.
            </p>
            <Link to="/book-a-stay" className="link-underline">
              Add overnight stays →
            </Link>
          </Reveal>
          <Reveal as="div" variant="scale" delay={120} className={styles.frame}>
            <Parallax
              speed={0.16}
              className={styles.frameInner}
              style={{ backgroundImage: `url(${mediaUrl(FEATURED.ceremonyDecks)})` }}
            >
              <span />
            </Parallax>
          </Reveal>
        </div>
      </section>

      {/* ---- Scrolling event photo banner ---- */}
      {eventPhotos.length > 0 && (
        <section className={styles.marqueeSection} aria-label="Event photo gallery">
          <Marquee images={rowOne} duration={70} direction="left" />
          {rowTwo.length > 0 && (
            <Marquee images={rowTwo} duration={85} direction="right" />
          )}
        </section>
      )}

      {/* ---- Event types with descriptions ---- */}
      <section className="section">
        <div className="container">
          <Reveal className={styles.typesHead}>
            <p className="eyebrow eyebrow--rule">What We Host</p>
            <h2>Every kind of celebration, beautifully set.</h2>
            <p className={styles.typesLede}>
              However you gather, Villa Rosa adapts to the occasion. Here are just
              a few of the events our estate was made for.
            </p>
          </Reveal>

          <div className={styles.typeGrid}>
            {EVENT_TYPES.map((e, i) => (
              <Reveal
                as="article"
                key={e.title}
                variant="up"
                delay={(i % 3) * 90}
                className={styles.typeCard}
              >
                <span className={styles.typeNum}>{String(i + 1).padStart(2, "0")}</span>
                <h3 className={styles.typeTitle}>{e.title}</h3>
                <p className={styles.typeDesc}>{e.description}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ---- Inquiry ---- */}
      <section id="inquire" className={`section section--alt ${styles.inquire}`}>
        <div className={`container ${styles.layout}`}>
          <Reveal as="div" variant="left" className={styles.intro}>
            <p className="eyebrow eyebrow--rule">Begin Planning</p>
            <h2>Tell us about your event.</h2>
            <p className={styles.sub}>
              Share a few details and our team will reach out to discuss
              availability, capacity, and how we can tailor Villa Rosa to your
              celebration — from décor and setup to catering arrangements.
            </p>
            <ul className={styles.assurances}>
              <li>Free, no-obligation inquiry</li>
              <li>Personal response within 24 hours</li>
              <li>Flexible packages for any group size</li>
            </ul>
          </Reveal>
          <Reveal as="div" variant="right" delay={120} className={styles.formCard}>
            <InquiryForm
              type="event"
              extras="event"
              submitLabel="Request event details"
              successTitle="Request received!"
              successBody="Thank you for considering Villa Rosa for your event. Our team will be in touch shortly to discuss the details."
            />
          </Reveal>
        </div>
      </section>
    </>
  );
}
